<script setup>
import { reactive, computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import WorkingHoursEditor from 'dashboard/components-next/appointments/WorkingHoursEditor.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  professional: {
    type: Object,
    default: null,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  // AP-03: backend validation message surfaced by the parent.
  serverError: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'submit']);

const { t } = useI18n();
const store = useStore();

const isEditing = computed(() => !!props.professional?.id);

const appointmentServicesContext = () => {
  const getter = store.getters.getCurrentAccountId;
  const accountId = typeof getter === 'function' ? getter() : getter;
  return { accountId };
};

// L-09: parse comma-separated registry string into array
const initialRegistries = (() => {
  const raw = props.professional?.registry || '';
  const arr = raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  return arr.length ? arr : [''];
})();

const form = reactive({
  name: props.professional?.name || '',
  specialty: props.professional?.specialty || '',
  registries: initialRegistries,
  email: props.professional?.email || '',
  phone: props.professional?.phone || '',
  color: props.professional?.color || '#3B82F6',
  buffer_minutes: props.professional?.buffer_minutes ?? 0,
  working_hours: props.professional?.working_hours || {},
  service_ids:
    props.professional?.service_ids ||
    (props.professional?.services || []).map(s => s.id),
  avatar: null,
});

// Avatar state — preview local URL ou URL persistida do servidor
const avatarPreview = ref(props.professional?.avatar_url || '');
const avatarMarkedForDelete = ref(false);

function handleAvatarUpload({ file, url }) {
  form.avatar = file;
  avatarPreview.value = url;
  avatarMarkedForDelete.value = false;
}

function handleAvatarDelete() {
  form.avatar = null;
  avatarPreview.value = '';
  avatarMarkedForDelete.value = true;
}

// H6: Focus management
const dialogRef = ref(null);
let triggerEl;
onMounted(() => {
  store.dispatch('appointmentServices/get', appointmentServicesContext());
  triggerEl = document.activeElement;
  const focusable = dialogRef.value?.querySelector(
    'input,select,button,textarea,[tabindex]:not([tabindex="-1"])'
  );
  focusable?.focus();
});
onUnmounted(() => triggerEl?.focus?.());

// L-01: services multi-select
const availableServices = computed(
  () => store.getters['appointmentServices/getAppointmentServices'] ?? []
);

function toggleService(id) {
  const idx = form.service_ids.indexOf(id);
  if (idx >= 0) form.service_ids.splice(idx, 1);
  else form.service_ids.push(id);
}

// L-12: contact autocomplete
const contactSuggestions = ref([]);
const showContactDropdown = ref(false);
let searchTimeout = null;

// AP-03: inline validation feedback instead of a silent return + disabled button.
const clientErrors = reactive({ name: '' });

function onNameInput() {
  clientErrors.name = '';
  clearTimeout(searchTimeout);
  showContactDropdown.value = form.name.length >= 2;
  if (form.name.length < 2) {
    contactSuggestions.value = [];
    return;
  }
  searchTimeout = setTimeout(async () => {
    try {
      const response = await ContactAPI.search(form.name, 1);
      const payload = response.data?.payload || [];
      contactSuggestions.value = Array.isArray(payload)
        ? payload.slice(0, 6)
        : [];
    } catch {
      contactSuggestions.value = [];
    }
  }, 250);
}

function onNameFocus() {
  if (form.name.length >= 2) showContactDropdown.value = true;
}

function pickContact(c) {
  form.name = c.name || form.name;
  if (c.email) form.email = c.email;
  if (c.phone_number) form.phone = c.phone_number;
  showContactDropdown.value = false;
  contactSuggestions.value = [];
}

function hideDropdown() {
  // Small delay so click on suggestion fires first
  setTimeout(() => {
    showContactDropdown.value = false;
  }, 150);
}

const handleSubmit = () => {
  clientErrors.name = form.name?.trim()
    ? ''
    : t('APPOINTMENTS.VALIDATION.NAME_REQUIRED');
  if (clientErrors.name) return;
  // L-09: join registries back to comma-separated string for backend compat
  const registry = form.registries.filter(r => r.trim()).join(', ');
  // Não enviar avatar: null junto — backend interpreta string vazia como
  // "remover avatar atual". Se nada mudou, omitir a key (avatar_url
  // continua igual).
  const payload = { ...form, registry };
  if (!form.avatar && !avatarMarkedForDelete.value) {
    delete payload.avatar;
  } else if (avatarMarkedForDelete.value && !form.avatar) {
    payload.avatar = '';
  }
  emit('submit', payload);
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    role="dialog"
    aria-modal="true"
    aria-labelledby="professional-form-title"
  >
    <div
      ref="dialogRef"
      class="bg-n-background rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
    >
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-strong"
      >
        <h2
          id="professional-form-title"
          class="text-lg font-semibold text-n-slate-12"
        >
          {{
            isEditing
              ? t('APPOINTMENTS.PROFESSIONALS.EDIT')
              : t('APPOINTMENTS.PROFESSIONALS.ADD')
          }}
        </h2>
        <button
          type="button"
          class="text-n-slate-10 hover:text-n-slate-11"
          :aria-label="t('APPOINTMENTS.CLOSE')"
          @click="$emit('close')"
        >
          <span class="i-lucide-x size-5" aria-hidden="true" />
        </button>
      </div>

      <div class="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        <!-- AP-03: backend validation message -->
        <div
          v-if="serverError"
          class="rounded-lg border border-n-ruby-5 bg-n-ruby-2 text-n-ruby-11 px-4 py-3 text-sm flex items-start gap-2"
          role="alert"
        >
          <span
            class="i-lucide-alert-circle size-4 mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <span>{{ serverError }}</span>
        </div>
        <!-- Avatar upload -->
        <div class="flex items-center gap-4 pb-4 border-b border-n-weak">
          <Avatar
            :src="avatarPreview"
            :name="form.name || '?'"
            :size="64"
            rounded-full
            allow-upload
            @upload="handleAvatarUpload"
            @delete="handleAvatarDelete"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12">
              {{
                t(
                  'APPOINTMENTS.PROFESSIONALS.FORM.AVATAR_LABEL',
                  'Foto do profissional'
                )
              }}
            </p>
            <p class="text-xs text-n-slate-11 mt-0.5">
              {{
                t(
                  'APPOINTMENTS.PROFESSIONALS.FORM.AVATAR_HINT',
                  'Clique no avatar para enviar uma foto. PNG, JPG ou WEBP até 5MB.'
                )
              }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- L-12: Name with contact autocomplete -->
          <div class="relative">
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.PROFESSIONALS.FORM.NAME') }}
              <span class="text-n-ruby-9" aria-hidden="true">&ast;</span>
            </label>
            <input
              v-model="form.name"
              type="text"
              :placeholder="
                t('APPOINTMENTS.PROFESSIONALS.FORM.NAME_PLACEHOLDER')
              "
              :class="clientErrors.name ? 'border-n-ruby-7' : 'border-n-strong'"
              class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
              @input="onNameInput"
              @focus="onNameFocus"
              @blur="hideDropdown"
            />
            <p v-if="clientErrors.name" class="mt-1 text-xs text-n-ruby-11">
              {{ clientErrors.name }}
            </p>
            <div
              v-if="showContactDropdown && contactSuggestions.length"
              class="absolute z-10 mt-1 w-full bg-n-background border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
              <button
                v-for="c in contactSuggestions"
                :key="c.id"
                type="button"
                class="w-full text-left px-3 py-2 hover:bg-n-alpha-1 text-sm"
                @mousedown.prevent="pickContact(c)"
              >
                <div class="font-medium text-n-slate-12">{{ c.name }}</div>
                <div class="text-xs text-n-slate-11">
                  {{ [c.email, c.phone_number].filter(Boolean).join(' · ') }}
                </div>
              </button>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.PROFESSIONALS.FORM.SPECIALTY') }}
            </label>
            <input
              v-model="form.specialty"
              type="text"
              class="w-full border border-n-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>

          <!-- L-09: Registry as array -->
          <div class="col-span-2">
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.PROFESSIONALS.FORM.REGISTRIES') }}
            </label>
            <div class="space-y-1">
              <div
                v-for="(r, idx) in form.registries"
                :key="idx"
                class="flex gap-2"
              >
                <input
                  v-model="form.registries[idx]"
                  type="text"
                  :placeholder="
                    t('APPOINTMENTS.PROFESSIONALS.FORM.REGISTRY_PLACEHOLDER')
                  "
                  class="flex-1 border border-n-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
                />
                <button
                  v-if="form.registries.length > 1"
                  type="button"
                  :aria-label="
                    t('APPOINTMENTS.PROFESSIONALS.FORM.REMOVE_REGISTRY')
                  "
                  class="text-n-ruby-9 hover:text-n-ruby-11 px-2 text-lg leading-none"
                  @click="form.registries.splice(idx, 1)"
                >
                  <span class="i-lucide-x size-4" aria-hidden="true" />
                </button>
              </div>
              <button
                type="button"
                class="text-xs text-n-brand hover:text-n-brand mt-1"
                @click="form.registries.push('')"
              >
                {{ t('APPOINTMENTS.PROFESSIONALS.FORM.ADD_REGISTRY') }}
              </button>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.PROFESSIONALS.FORM.EMAIL') }}
            </label>
            <input
              v-model="form.email"
              type="email"
              class="w-full border border-n-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.PROFESSIONALS.FORM.PHONE') }}
            </label>
            <input
              v-model="form.phone"
              type="tel"
              class="w-full border border-n-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ t('APPOINTMENTS.PROFESSIONALS.FORM.BUFFER_MINUTES') }}
            </label>
            <input
              v-model.number="form.buffer_minutes"
              type="number"
              min="0"
              class="w-full border border-n-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('APPOINTMENTS.PROFESSIONALS.FORM.COLOR') }}
          </label>
          <div class="flex items-center gap-3">
            <input
              v-model="form.color"
              type="color"
              class="h-9 w-16 border border-n-strong rounded cursor-pointer"
            />
            <span class="text-sm text-n-slate-11">{{ form.color }}</span>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('APPOINTMENTS.PROFESSIONALS.FORM.WORKING_HOURS') }}
          </label>
          <WorkingHoursEditor v-model="form.working_hours" />
        </div>

        <!-- L-01: Services offered multi-select -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('APPOINTMENTS.PROFESSIONALS.FORM.SERVICES_OFFERED') }}
          </label>
          <div
            v-if="!availableServices.length"
            class="text-sm text-n-slate-11 italic"
          >
            {{ t('APPOINTMENTS.PROFESSIONALS.FORM.NO_SERVICES_YET') }}
          </div>
          <div
            v-else
            class="space-y-2 max-h-48 overflow-y-auto rounded-lg border border-n-weak p-3"
          >
            <label
              v-for="srv in availableServices"
              :key="srv.id"
              class="flex items-center gap-2 text-sm cursor-pointer"
            >
              <input
                type="checkbox"
                :value="srv.id"
                :checked="form.service_ids.includes(srv.id)"
                class="rounded border-n-strong"
                @change="toggleService(srv.id)"
              />
              <span class="text-n-slate-12">{{ srv.name }}</span>
              <span class="text-n-slate-11 text-xs">
                {{
                  `(${srv.duration_minutes} ${t(
                    'APPOINTMENTS.PROFESSIONALS.FORM.DURATION_MIN_SUFFIX'
                  )})`
                }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div
        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
      >
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-1 rounded-lg"
          @click="$emit('close')"
        >
          {{ t('APPOINTMENTS.PROFESSIONALS.FORM.CANCEL') }}
        </button>
        <button
          type="button"
          :disabled="isSubmitting"
          class="px-4 py-2 text-sm font-medium text-white bg-n-brand hover:bg-n-brand/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          @click="handleSubmit"
        >
          {{ t('APPOINTMENTS.PROFESSIONALS.FORM.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
